import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, LogOut, User, Key, Search, Hash, Image as ImageIcon, Instagram, Users, MessageSquare, Compass, Music, Megaphone, FileText, Gift, Dna, Layers, Film, Zap, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/app/Footer";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import BrandDnaBanner from "@/components/app/BrandDnaBanner";
import TippingModal from "@/components/app/TippingModal";
import RecentAnalyses from "@/components/app/RecentAnalyses";
import { useBrandDnaSummary } from "@/lib/useBrandDna";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnalysePyramid({ navigate }) {
  const [platform, setPlatform] = useState("instagram");

  const TOOLS = {
    instagram: [
      { to: "/post-check?platform=instagram", icon: ImageIcon, label: "Post-/Reel-Check", desc: "Einzelner Post oder Reel — Hook, Caption & Hashtags optimieren." },
      { to: "/tools/story-check", icon: Sparkles, label: "Story-Check", desc: "Story-Screenshot hochladen — Vision-AI bewertet Hook, CTA, Sticker." },
      { to: "/hashtag-check?platform=instagram", icon: Hash, label: "Hashtag-Check", desc: "Hashtag-Volumen, Top-Posts & Strategie." },
      { to: "/comment-goldmine?platform=instagram", icon: MessageSquare, label: "Comment Goldmine", desc: "Kommentare → Audience-Pains, FAQs, Content-Ideen." },
      { to: "/trends?tab=ig-trending", icon: Compass, label: "Virale Posts (Nische)", desc: "Top-Posts deiner Nische — sortiert nach Engagement." },
    ],
    tiktok: [
      { to: "/post-check?platform=tiktok", icon: Film, label: "Video-Check", desc: "Einzelnes TikTok-Video — Hook & Performance." },
      { to: "/hashtag-check?platform=tiktok", icon: Hash, label: "Hashtag-Check", desc: "TikTok-Hashtag: Top-Videos & Strategie." },
      { to: "/comment-goldmine?platform=tiktok", icon: MessageSquare, label: "Comment Goldmine", desc: "Kommentare → Audience-Pains & Ideen." },
      { to: "/trends?tab=tt-sounds", icon: Music, label: "Trending Sounds", desc: "Hot-Audios der letzten 24-72h." },
    ],
    facebook: [
      { to: "/facebook-tools?tab=ads", icon: Megaphone, label: "Ads-Spy", desc: "Aktive Werbeanzeigen der Konkurrenz." },
      { to: "/facebook-tools?tab=page", icon: FileText, label: "Page-Audit", desc: "Facebook-Page komplett analysieren." },
      { to: "/facebook-tools?tab=group", icon: Users, label: "Group Listener", desc: "Content-Ideen aus Nischen-Gruppen." },
    ],
  };

  return (
    <div className="mb-10 sm:mb-12" data-testid="analyse-pyramid">
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        KI-Analyse Tools — Plattform wählen
      </p>

      <div className="flex justify-center gap-2 mb-6 flex-wrap" data-testid="platform-switch">
        {[
          { id: "instagram", label: "Instagram", icon: Instagram },
          { id: "tiktok", label: "TikTok", icon: Zap },
          { id: "facebook", label: "Facebook", icon: Users },
        ].map(p => (
          <button
            key={p.id}
            onClick={() => setPlatform(p.id)}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl border text-sm font-medium transition-all ${
              platform === p.id
                ? "border-cyan-500 bg-cyan-500/10 text-cyan-300 shadow-[0_0_20px_-8px_rgba(0,212,255,0.6)]"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
            }`}
            data-testid={`pyramid-platform-${p.id}`}
          >
            <p.icon className="w-4 h-4" />{p.label}
          </button>
        ))}
      </div>

      {/* Hero — only for IG/TT (Profile Audit) */}
      {platform !== "facebook" && (
        <button
          onClick={() => navigate(`/analyse?platform=${platform}`)}
          className="group relative w-full max-w-2xl mx-auto block p-6 sm:p-7 rounded-2xl bg-gradient-to-br from-cyan-500/15 via-cyan-400/10 to-cyan-500/5 border border-cyan-500/40 hover:border-cyan-400/60 transition-all overflow-hidden"
          data-testid="hero-profile-audit-btn"
        >
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl group-hover:bg-cyan-500/30 transition-all" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 text-left">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Search className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Komplette Profil-Analyse</h3>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold" style={{ background: 'rgba(200,255,77,0.12)', color: '#C8FF4D', border: '1px solid rgba(200,255,77,0.3)' }}>Top Tool</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">Insta &amp; TikTok</span>
              </div>
              <p className="text-sm text-zinc-300 mb-2">Ghost-Follower, Bio, Content, Hashtags, Compliance, Algorithmus & 90-Tage-Wachstumsplan + Konkurrenz-Vergleich.</p>
              <p className="text-xs text-emerald-400 font-medium">✓ Einmal Premium → unlimited Audits, für immer.</p>
            </div>
            <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </div>
        </button>
      )}

      {/* Pyramide Sub-Tools */}
      <div className="mt-3 flex justify-center">
        <div className="w-full max-w-2xl flex flex-col items-center">
          {platform !== "facebook" && <div className="w-px h-4 bg-zinc-800" />}
          <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-3">
            {platform === "instagram" ? "Oder gezielter prüfen — Instagram-Tools" :
             platform === "tiktok" ? "Oder gezielter prüfen — TikTok-Tools" :
             "Facebook-Tools"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto">
        {TOOLS[platform].map(t => (
          <button
            key={t.to}
            onClick={() => navigate(t.to)}
            className="group p-4 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-cyan-500/40 transition-all text-left"
            data-testid={`tool-${t.to.split('?')[0].replace('/', '')}`}
          >
            <t.icon className="w-4 h-4 text-cyan-400 mb-2 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-semibold text-white mb-1">{t.label}</p>
            <p className="text-[11px] text-zinc-500 leading-relaxed">{t.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { summary: dna } = useBrandDnaSummary();
  const [showRecovery, setShowRecovery] = useState(false);
  const [showTipping, setShowTipping] = useState(false);
  const [tippingVariant, setTippingVariant] = useState("manual");

  // Trinkgeld-Auto-Popup:
  //   • Erstes Mal HEUTE (= sofort beim ersten Dashboard-Besuch) für alle bezahlenden Kunden
  //   • Danach alle 14 Tage automatisch wieder
  //   • Manuelles Öffnen via Header-Herz funktioniert immer (außerhalb dieser Logik)
  useEffect(() => {
    if (!user || !user.plan_active) return;
    if (user.plan === "free") return;
    try {
      const KEY = "tipping_last_shown_v2";
      const INTERVAL_MS = 14 * 86400000; // 14 Tage in Millisekunden
      const now = Date.now();
      const lastShown = localStorage.getItem(KEY);
      // Wenn noch nie gezeigt ODER mehr als 14 Tage her: anzeigen
      const shouldShow = !lastShown || (now - parseInt(lastShown, 10)) >= INTERVAL_MS;
      if (!shouldShow) return;
      const t = setTimeout(() => {
        setTippingVariant("auto");
        setShowTipping(true);
        localStorage.setItem(KEY, now.toString());
      }, 3500);
      return () => clearTimeout(t);
    } catch (_) {}
  }, [user]);

  return (
    <div className="min-h-screen bg-[#09090b] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[300px] blur-[100px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(200,255,77,0.07) 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        {/* User Menu */}
        <div className="absolute top-4 right-4 sm:right-6 flex items-center gap-3 z-20" data-testid="user-menu">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-cyan-400" />
            </div>
            <span className="text-xs text-zinc-400 hidden sm:inline">{user?.name || user?.email}</span>
          </div>
          {user?.plan_active && (
            <button
              onClick={() => navigate("/affiliate-invite")}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{ background: 'rgba(200,255,77,0.10)', border: '1px solid rgba(200,255,77,0.35)', color: '#C8FF4D' }}
              title="Premium Affiliate · 45 % Provision"
              data-testid="nav-affiliate-btn"
            >
              <Gift className="w-3.5 h-3.5" />
              Premium Affiliate
            </button>
          )}
          {user?.plan_active && (
            <button
              onClick={() => navigate("/affiliate-invite")}
              className="sm:hidden p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
              style={{ color: '#C8FF4D' }}
              title="Premium Affiliate"
              data-testid="nav-affiliate-btn-mobile"
            >
              <Gift className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => navigate("/brand-dna")}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-400/50 transition-colors"
            title="Brand DNA · Marken-Identität in 10 Schritten"
            data-testid="nav-brand-dna-btn"
          >
            <Dna className="w-3.5 h-3.5" />
            Brand DNA
          </button>
          <button
            onClick={() => navigate("/brand-dna")}
            className="sm:hidden p-1.5 rounded-lg hover:bg-zinc-800 text-cyan-400 transition-colors"
            title="Brand DNA"
            data-testid="nav-brand-dna-btn-mobile"
          >
            <Dna className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setTippingVariant("manual"); setShowTipping(true); }}
            className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 transition-all hover:scale-110 group"
            title="Uns ein Trinkgeld geben"
            data-testid="show-tipping-btn"
          >
            <Heart className="w-3.5 h-3.5 group-hover:fill-pink-500 group-hover:text-pink-500 transition-colors" />
          </button>
          <button onClick={() => setShowRecovery(!showRecovery)} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-cyan-400 transition-colors" title="Recovery-Code" data-testid="show-recovery-btn">
            <Key className="w-3.5 h-3.5" />
          </button>
          <button onClick={logout} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-red-400 transition-colors" title="Ausloggen" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
        {showRecovery && user?.recovery_code && (
          <div className="absolute top-14 right-4 sm:right-6 z-30 p-4 rounded-xl bg-zinc-900 border border-zinc-800 shadow-xl" data-testid="recovery-popup">
            <p className="text-xs text-zinc-400 mb-2">Dein Recovery-Code:</p>
            <p className="text-lg font-mono font-bold tracking-widest text-center" style={{ color: '#00D4FF' }}>{user.recovery_code}</p>
            <button onClick={() => { navigator.clipboard.writeText(user.recovery_code); toast.success("Code kopiert!"); }} className="mt-2 text-xs text-cyan-400 hover:text-cyan-300 w-full text-center">Kopieren</button>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-10 sm:mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
               style={{ borderColor: 'rgba(0,212,255,0.3)', background: 'rgba(0,212,255,0.05)' }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#00D4FF' }} />
            <span className="text-xs font-medium tracking-wider uppercase" style={{ color: '#7DD8F4' }}>Social IQ · Dein KI-Toolkit</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="text-white">Willkommen zurück</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Wähle dein Tool — fertige Karussells, Profil-Audit, Hashtag-Strategie und mehr.
          </p>
        </div>

        {/* Brand DNA — Card if completed, Banner if pending/empty */}
        {dna ? (
          <div className="max-w-md mx-auto mb-10 sm:mb-12" data-testid="dashboard-dna-card">
            <BrandDnaCard summary={dna} />
          </div>
        ) : (
          <div className="max-w-2xl mx-auto mb-10 sm:mb-12">
            <BrandDnaBanner />
          </div>
        )}

        {/* Recent Analyses — only shows if user has any */}
        <RecentAnalyses />

        {/* ─── Hero: Karussell-Generator (NEU) ─── */}
        <div className="mb-10 sm:mb-12" data-testid="carousel-hero">
          <button
            onClick={() => navigate("/content")}
            className="w-full text-left group relative overflow-hidden rounded-2xl p-6 sm:p-8 transition-all hover:scale-[1.005]"
            style={{
              background: 'linear-gradient(135deg, rgba(0,212,255,0.10) 0%, rgba(200,255,77,0.08) 100%)',
              border: '1px solid rgba(0,212,255,0.35)',
            }}
            data-testid="content-hero-cta"
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-30 blur-3xl pointer-events-none"
                 style={{ background: 'radial-gradient(circle, rgba(200,255,77,0.6) 0%, transparent 70%)' }} />
            <div className="relative flex items-start gap-5 flex-col sm:flex-row">
              <div className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                   style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}>
                <Layers className="w-7 h-7 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold"
                        style={{ background: 'rgba(200,255,77,0.15)', color: '#C8FF4D', border: '1px solid rgba(200,255,77,0.3)' }}>NEU</span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Content Generator</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Content Generator
                </h3>
                <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                  Karussell-Slides, <span className="text-cyan-300 font-semibold">Titelbilder</span>, Story-Prompts — der Creative Director stellt gezielte Fragen und baut <span className="text-cyan-300 font-semibold">perfekte Bild-Prompts</span> für dich. Kostenlos, unlimitiert.
                </p>
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-black px-3 py-1.5 rounded-lg group-hover:scale-105 transition-transform"
                     style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}>
                  Content erstellen <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          </button>
        </div>
        {/* ─── End Karussell-Hero ─── */}

        {/* ─── Analyse-Tools Pyramide ─── */}
        <AnalysePyramid navigate={navigate} />
        {/* ─── End Pyramide ─── */}

      </div>
      <Footer variant="app" />
      <TippingModal open={showTipping} onClose={() => setShowTipping(false)} variant={tippingVariant} />
    </div>
  );
}
